import { useEffect, useState } from "react";

import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  SwipeableDrawer,
  Typography,
  useMediaQuery,
} from "@mui/material";

import { Close } from "@mui/icons-material";
import axios from "axios";
import { Controller, useForm } from "react-hook-form";

import TutredRating from "@/components/elements/TutredRating";
import { TutredTextArea } from "@/components/elements/TutredTextArea/TutredTextArea";
import useFirebaseContext from "@/hooks/useFirebaseAuth";

interface RateLessonFormInputs {
  rating: number;
  review: string;
}

interface RateLessonDialogProps {
  open: boolean;
  onClose: () => void;
  lessonId: string;
  onSubmitted?: () => void;
}

const RateLessonDialog = ({
  open,
  onClose,
  lessonId,
  onSubmitted,
}: RateLessonDialogProps) => {
  const {
    control,
    handleSubmit,
    reset,
    watch,
    setError,
    formState: { errors },
  } = useForm<RateLessonFormInputs>({
    defaultValues: {
      rating: 0,
      review: "",
    },
  });
  const { authenticatedAxios } = useFirebaseContext();
  const ratingValue = watch("rating");
  const isSm = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  const onSubmitForm = (data: RateLessonFormInputs) => {
    authenticatedAxios
      .post(`/reviews/${lessonId}`, {
        rating: data.rating,
        review: data.review,
      })
      .then(() => {
        onSubmitted?.();
        handleClose();
      })
      .catch((error: unknown) => {
        let message = "Something went wrong, please try again later.";
        if (axios.isAxiosError(error)) {
          const data = error.response?.data as { message?: string } | undefined;
          if (data?.message) {
            message = data.message;
          }
        }
        setError("root", { message });
      });
  };

  const handleClose = () => {
    reset();
    setIsSheetOpen(false);
    onClose();
  };

  useEffect(() => {
    if (open && isSm) {
      const timer = setTimeout(() => {
        setIsSheetOpen(true);
      }, 100);
      return () => clearTimeout(timer);
    }
    if (!open) {
      setIsSheetOpen(false);
    }
    return undefined;
  }, [open, isSm]);

  const handleSheetClose = () => {
    setIsSheetOpen(false);
    handleClose();
  };

  const handleSheetOpen = () => {
    setIsSheetOpen(true);
  };

  const ratingForm = (
    <form onSubmit={handleSubmit(onSubmitForm)}>
      <Stack spacing={3} alignItems="center" sx={{ mt: 1 }}>
        <Box
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
          }}
        >
          <Controller
            name="rating"
            control={control}
            rules={{
              required: "Please select a rating",
              min: {
 value: 0.5,
message: "Please select a rating" 
},
            }}
            render={({ field }) => (
              <TutredRating
                {...field}
                value={field.value || 0}
                onChange={(_, value) => field.onChange(value ?? 0)}
                iconSize={36}
                filledColor="brand.green"
                emptyColor="brand.green"
                emptyOpacity={1}
                useOutlineIcon
                precision={0.5}
              />
            )}
          />
          <Box
            sx={{
              position: "absolute",
              right: 0,
            }}
          >
            {ratingValue > 0 ? (
              <Typography variant="text18L" component="span">
                {Number.isInteger(ratingValue)
                  ? ratingValue
                  : ratingValue.toFixed(1)}
                /5
              </Typography>
            ) : null}
          </Box>
        </Box>
        {errors.rating ? (
          <Typography variant="caption" color="error">
            {errors.rating.message}
          </Typography>
        ) : null}

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
          }}
        >
          <Typography variant="text13L" sx={{ mb: 1 }}>
            Note (Optional)
          </Typography>
          <Controller
            name="review"
            control={control}
            defaultValue=""
            render={({ field, fieldState: { error } }) => (
              <TutredTextArea
                {...field}
                placeholder="Enter your feedback"
                maxLength={300}
                rows={2}
                color="secondary"
                error={!!error}
                helperText={error?.message}
                sx={{ width: "100%" }}
              />
            )}
          />
        </Box>

        {errors.root ? (
          <Typography variant="caption" color="error" textAlign="center">
            {errors.root.message}
          </Typography>
        ) : null}

        <Button
          type="submit"
          variant="contained"
          fullWidth
          disabled={ratingValue === 0}
          sx={{
            bgcolor: "primary.main",
            color: "white",
            py: 1.5,
            textTransform: "none",
            fontWeight: 600,
            "&:hover": {
              bgcolor: "primary.main",
              opacity: 0.9,
            },
            "&:disabled": {
              bgcolor: "action.disabledBackground",
              color: "action.disabled",
            },
          }}
        >
          Submit
        </Button>
      </Stack>
    </form>
  );

  if (isSm) {
    return (
      <SwipeableDrawer
        anchor="bottom"
        open={isSheetOpen}
        onClose={handleSheetClose}
        onOpen={handleSheetOpen}
        ModalProps={{ keepMounted: true }}
        sx={{ zIndex: 10000 }}
        slotProps={{
          paper: {
            sx: {
              height: "55vh",
              maxHeight: "55vh",
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
            },
          },
        }}
      >
        <Typography variant="h2" sx={{
 mb: 1,
mt: 2,
textAlign: "center" 
}}>
          How was your{" "}
          <Typography variant="h2Serif" component="span">
            lesson?
          </Typography>
        </Typography>
        <Typography variant="text13L" textAlign="center">
          Your feedback makes us better
        </Typography>

        <Box
          sx={{
            overflow: "auto",
            px: 2,
            pt: 3,
            pb: 2,
          }}
        >
          {ratingForm}
        </Box>
      </SwipeableDrawer>
    );
  }

  return (
    <Dialog
      open={open}
      maxWidth="xs"
      fullWidth
      onClose={handleClose}
      scroll="body"
    >
      <IconButton
        aria-label="Close rate lesson dialog"
        onClick={handleClose}
        sx={{
          position: "absolute",
          top: -24,
          right: -24,
          color: "common.white",
        }}
      >
        <Close />
      </IconButton>
      <DialogContent sx={{
 px: 3,
pb: 3 
}}>
        <Stack
          direction="column"
          alignItems="center"
          display="flex"
          sx={{ mb: 3 }}
        >
          <Typography variant="h2" sx={{
 mb: 1,
mt: 2,
textAlign: "center" 
}}>
            How was your{" "}
            <Typography variant="h2Serif" component="span">
              lesson?
            </Typography>
          </Typography>
          <Typography variant="text13L">Your feedback makes us better</Typography>
        </Stack>
        {ratingForm}
      </DialogContent>
    </Dialog>
  );
};

export default RateLessonDialog;
